﻿/* eslint-disable no-unused-vars*/
import * as Res from "./res";
import { dbfetch } from "./dbfetch";
import { getApiConfig } from "./context";
import { createElement, unsafeSetHTML } from "./_dom";
import { runCallbacks } from './asyncViews';
import { init as initStickyCaptions } from "./_stickyCaption";
import { tabs } from "./tabs";
import { init as initToolbuttons } from "./actionButtons";
import { parseColor, lightOrDark, rgbToHsl } from "./colorutils";
import { createSmallSpinner } from "./spinner";
import { ensureVisible } from "./_ensureVisible";

const rgAnchor = /^#anchor_([^_]+).*$/;

function $(id) {
	if (id && id.length) {
		if (id.charAt(0) === '#') {
			id = id.substring(1);
		}
		return document.getElementById(id);
	}
	return null;
}


function reorderTables(tables, qs) {
	const m = rgAnchor.exec(location.hash);
	let hash;
	if (m || qs) {
		var tid = m ? m[1] : qs;
		for (let i = 0; i < tables.length; i++)
			if (tables[i].id == tid) {
				if (i) tables.unshift(tables.splice(i, 1)[0]);
				if (m) hash = m[0];
				break;
			}
	}
	return hash;
}

async function loadHtml(url, options) {
	const response = await dbfetch(url, options);
	if (response.ok) {
		const ct = response.headers.get("Content-Type");
		if (/^text\/html(;|$)/.test(ct)) {
			const html = await response.text();
			return html;
		}
		throw new Error("Invalid content: " + ct);
	}
	throw new Error(`ERROR: ${response.status} ${response.statusText}`);
}

const progressObserver = new IntersectionObserver(entries => entries.forEach(async entry => {
	if (entry.isIntersecting) {
		const loader = entry.target;
		const pane = loader.parentNode;
		progressObserver.unobserve(loader);
		const url = pane.dataset.url;
		const html = await loadHtml(url);
		pane.setAttribute("aria-busy", "false");
		setHtml(loader, html, pane.dataset.hash);
		initToolbuttons(pane);
		loader.remove();
	}
}), {});

function createTab(tabDef, active) {
	const parsedColor = parseColor(tabDef.color);
	const parsedHsl = rgbToHsl(parsedColor);
	const link = createElement("a", {
		"role": "tab",
		"href": "#anchor_" + tabDef.id,
		"class": ["v3-detail-tabs__item", "v3-tabs__item", active && "v3-tabs__item--active", parsedColor && `v3-hint-color--${lightOrDark(parsedColor)}`],
		"aria-owns": "v3-detail-tab-" + tabDef.id,
		"aria-selected": active.toString(),
		"tabindex": !active && -1,
		"data-tabid": tabDef.id,
		"--v3-hint-color": parsedHsl && `hsl(${parsedHsl.h},${parsedHsl.s}%,${parsedHsl.l}%)`,
		"--v3-hint-color-h": parsedHsl && parsedHsl.h,
		"--v3-hint-color-s": parsedHsl && parsedHsl.s,
		"--v3-hint-color-l": parsedHsl && parsedHsl.l
	});
	let textContainer = link;
	if (tabDef.icon != "") {
		link.classList.add("ui-icon-custom");
		link.setAttribute("data-icon", tabDef.icon);
		if (tabDef.iconOnly) {
			link.classList.add("ui-icon-only");
			const span = createElement("span", { "class": "ui-icontext" });
			link.append(span);
			textContainer = span;
		}
	}
	textContainer.append(tabDef.name);

	const li = createElement("li", {
		"role": "none",
		"class": [ "v3-tabs__cell" ],
	}, link);
	return li;
}

function handleTabClick({ currentTarget: tabList, detail: item }) {
	if (item && !item.classList.contains("v3-tabs__item--active")) {
		const newTabId = item.dataset.tabid;
		const newPane = document.getElementById("v3-detail-tab-" + newTabId);
		if (newPane) {
			const tabControl = tabs(tabList);
			var activeTab = tabControl.activeTab;
			var activeTabId = activeTab.dataset.tabid;
			var activePane = document.getElementById("v3-detail-tab-" + activeTabId);
			activePane.classList.add("v3-detail-pane--hidden");
			tabControl.activeTab = item;
			tabControl.forceResize();
			newPane.classList.remove("v3-detail-pane--hidden");
		}
	}
}

function loadDetailsAsTabs(data) {
	const tables = data.tables;
	const before = $(data.before);
	if (tables.length) {
		var m = rgAnchor.exec(location.hash);
		var hash = m && m[0], tid = m && m[1];

		const fragment = [];

		const nav = createElement("nav", { id: "td-detailtabs" });
		fragment.push(nav);

		const ul = createElement("ul", {
			"role": "tablist",
			"class": "v3-detail-tabs v3-tabs",
			"on": {
				"tab.click": handleTabClick 
			}
		});
		nav.append(ul);

		let activeTabInfo = null;
		let prevLi = null;
		for (var i = tables.length - 1; i >= 0; i--) {
			var tt = tables[i];
			// var tabbefore = tabid + "_before";
			var active = activeTabInfo == null && (i === 0 || tt.id == tid);

			var li = createTab(tt, active);
			if (active)
				activeTabInfo = tt;

			ul.insertBefore(li, prevLi);
			prevLi = li;

			const url = new URL(tt.url);
			url.searchParams.set("back", getApiConfig("thisUrl"));

			const loader = createSmallSpinner(String.format(Res.getString("form.load_detail"), tt.name));
			const parsedColor = parseColor(tt.color);
			const parsedHsl = rgbToHsl(parsedColor);
			const detailPane = createElement("div", {
				id: "v3-detail-tab-" + tt.id, 
				role: "tabpanel",
				class: ["td-detailtab v3-detail-pane", !active && " v3-detail-pane--hidden"],
				"aria-busy": "true",
				"--v3-current-table-hint-color-h": parsedHsl && parsedHsl.h,
				"--v3-current-table-hint-color-s": parsedHsl && parsedHsl.s,
				"--v3-current-table-hint-color-l": parsedHsl && parsedHsl.l,
				dataset: { url, hash }
			}, loader);

			progressObserver.observe(loader);
			fragment.push(detailPane);
		}
		before.before(...fragment);
		tabs(ul, { tabrole: "tab", classPrefix: [ "v3-detail-tabs", "v3-tabs" ] });
	}
	before.remove();
}

function createTableListRequest(before, { /*id,*/ name, url, hash, callback }) {
	return function () {
		const listObserver = new IntersectionObserver(entries => entries.forEach(async entry => {
			if (entry.isIntersecting) {
				const loader = entry.target;
				listObserver.unobserve(loader);
				const html = await loadHtml(url);
				setHtml(loader, html, hash);
				if (callback) {
					callback(loader);
				}
				loader.remove();
			}
		}), {});
		const loader = createSmallSpinner(String.format(Res.getString("form.load_detail"), name));
		before.before(loader);
		listObserver.observe(loader);
	};
}

function loadDetailsAsList(data) {
	var tables = data.tables;
	var before = $(data.before);
	if (tables.length) {
		let lastCallback = null;
		var hash = reorderTables(tables);
		for (let i = tables.length - 1; i >= 0; i--) {
			const tt = tables[i];
			const url = new URL(tt.url);
			url.searchParams.set("back", getApiConfig("thisUrl"));
			lastCallback = createTableListRequest(before, { name: tt.name, url: url.toString(), hash, callback: lastCallback });
		}
		if(lastCallback)
			lastCallback();
	}
	else {
		before.remove();
	}
}

function loadDetails(data) {
	return data.tabs ? loadDetailsAsTabs(data) : loadDetailsAsList(data);
}

function appendNoMatch(after, name) {
	var nomatch = document.getElementById("td-search-nomatch");
	if (!nomatch) {
		nomatch = createElement("div", { "id": "td-search-nomatch" },
			createElement("section", { "class": "v3-layout-simple v3-layout" },
				createElement("header", { "class": "v3-layout__header" },
					createElement("h2", { "class": "hdr v3-layout__title" }, Res.getString("searchresults.no_records.title")),
					createElement("div", {
						"id": "td-search-nomatch-list",
						"class": "v3-layout__description"
					},
						String.format(Res.getString("searchresults.no_records.description"), name)
					)
				)
			)
		);
		after.after(nomatch);
	} else {
		var list = document.getElementById("td-search-nomatch-list");
		list.append(", " + name);
	}
}

async function setHtml(before, html, hash) {
	const parent = before.parentElement;
	await unsafeSetHTML(before, html, "before");
	initStickyCaptions(parent);
	runCallbacks(parent);
	ensureVisible(hash);
}

function createTableSearchRequest(before, { id, name, keywords, highlight, hash, callback }) {
	var label = String.format(Res.getString("searchresults.progress"), name);
	return async function () {
		var url = "searchtable.aspx?" + new URLSearchParams({
			t: id,
			keywords: keywords,
			back: getApiConfig("thisUrl")
		});

		const loader = createSmallSpinner(label);
		before.before(loader);

		var html = "";
		try {
			html = await loadHtml(url);
		} catch (e) {
			console.error("Error while searching", name, e);
		}

		loader.remove();

		if (html != "") {
			await setHtml(before, html, hash);
			if (callback) {
				var buttonBlock = createElement("div", {
						class: "v3-search-continue"
					},
					createElement("button", {
						"type": "button",
						"class": "v3-search-continue__button v3-button ui-button small",
						"on": {
							"click": e => {
								e.preventDefault();
								buttonBlock.remove();
								callback();
							}
						}
					},
						Res.getString("searchresults.action.continue")
					)
				);
				before.before(buttonBlock);
			}
		} else {
			appendNoMatch(before, name);
			if(callback) callback();
		}
		if (!callback) {
			before.remove();
		}
	};
}

function createAttachmentSearchRequest(before, { keywords }) {
	var name = Res.getString("searchresults.attachments");
	var label = String.format(Res.getString("searchresults.progress"), Res.getString("searchresults.attachments"));
	return async function () {
		var url = "searchattachments.aspx?" + new URLSearchParams({
			keywords: keywords,
			back: getApiConfig("thisUrl")
		});

		const loader = createSmallSpinner(label);
		before.before(loader);

		var html = "";
		try {
			html = await loadHtml(url);
		} catch (e) {
			console.error("Error while searching", name, e);
		}

		loader.remove();

		if (html != "") {
			setHtml(before, html);
		} else {
			appendNoMatch(before, name);
		}

		before.remove();
	};
}


function searchAll(data) {
	var tables = data.tables;
	var before = $(data.before);
	if (tables.length) {
		var keywords = data.keywords;
		var highlight = data.highlight;
		var hash = reorderTables(tables, +new URLSearchParams(location.search).get("t"));

		var lastCallback = null;
		if (data.attachments) {
			lastCallback = createAttachmentSearchRequest(before, { keywords, highlight });
		}
		for (let i = tables.length - 1; i >= 0; i--) {
			lastCallback = createTableSearchRequest(before, { id: tables[i].id, name: tables[i].name, keywords, highlight, hash, callback: lastCallback });
		}
		if(lastCallback)
			lastCallback();
	}
	else {
		before.remove();
	}
}

function initLoadDetails(ctx) {
	(ctx || document).querySelectorAll("[data-load-details]").forEach(el => loadDetails(JSON.parse(el.dataset.loadDetails)));
}

function initSearchAll(ctx) {
	(ctx || document).querySelectorAll("[data-search-all]").forEach(el => searchAll(JSON.parse(el.dataset.searchAll)));
}

export { initLoadDetails, initSearchAll };
